import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import {
  AlertCircle, Loader, Package, PackageX, PackageCheck,
  RefreshCw, AlertTriangle, TrendingDown, FolderOpen, Archive
} from 'lucide-react';

const StockStatus = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (selectedSite) {
      fetchStockStatus();
    } else {
      setLoading(false);
      setError('Please select a site');
    }
  }, [selectedSite]);

  const fetchStockStatus = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/analytics/stock-status.php?siteId=${selectedSite.id}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to fetch stock status');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStockHealthScore = () => {
    if (!data?.summary) return 0;
    const total = data.summary.in_stock + data.summary.out_of_stock;
    if (total === 0) return 100;
    return Math.round((data.summary.in_stock / total) * 100);
  };

  const getHealthColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthBg = (score) => {
    if (score >= 90) return 'bg-green-100 border-green-300';
    if (score >= 70) return 'bg-yellow-100 border-yellow-300';
    return 'bg-red-100 border-red-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading stock status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-red-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  const healthScore = getStockHealthScore();

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Package className="h-8 w-8 text-blue-600" />
              Stock Status Dashboard
            </h1>
            <p className="text-gray-600 mt-2">Monitor inventory levels and out-of-stock products</p>
          </div>
          <button
            onClick={fetchStockStatus}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {data && (
          <>
            {/* Health Score */}
            <div className={`mb-6 p-6 rounded-lg border-2 ${getHealthBg(healthScore)}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Stock Health Score</h2>
                  <p className="text-sm text-gray-600">Percentage of products currently in stock</p>
                </div>
                <div className={`text-5xl font-bold ${getHealthColor(healthScore)}`}>
                  {healthScore}%
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-gray-500">Total Products</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{data.summary?.total_products || 0}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <PackageCheck className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-gray-500">In Stock</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{data.summary?.in_stock || 0}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <PackageX className="h-5 w-5 text-red-600" />
                  <span className="text-sm text-gray-500">Out of Stock</span>
                </div>
                <p className="text-2xl font-bold text-red-600">{data.summary?.out_of_stock || 0}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm text-gray-500">Low Stock</span>
                </div>
                <p className="text-2xl font-bold text-yellow-600">{data.summary?.low_stock || 0}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Archive className="h-5 w-5 text-gray-600" />
                  <span className="text-sm text-gray-500">Disabled</span>
                </div>
                <p className="text-2xl font-bold text-gray-600">{data.summary?.disabled || 0}</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 ${
                      activeTab === 'overview'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Overview
                  </button>
                  <button
                    onClick={() => setActiveTab('oos')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 flex items-center gap-2 ${
                      activeTab === 'oos'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Out of Stock
                    {data.summary?.out_of_stock > 0 && (
                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
                        {data.summary.out_of_stock}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('low')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 flex items-center gap-2 ${
                      activeTab === 'low'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Low Stock
                    {data.summary?.low_stock > 0 && (
                      <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">
                        {data.summary.low_stock}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('categories')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 flex items-center gap-2 ${
                      activeTab === 'categories'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Categories
                    {data.empty_categories?.length > 0 && (
                      <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">
                        {data.empty_categories.length} empty
                      </span>
                    )}
                  </button>
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Stock Distribution Chart (simple bar) */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Stock Distribution</h3>
                      <div className="h-8 rounded-lg overflow-hidden flex">
                        {data.summary?.in_stock > 0 && (
                          <div
                            className="bg-green-500 flex items-center justify-center text-white text-xs font-medium"
                            style={{ width: `${(data.summary.in_stock / (data.summary.in_stock + data.summary.out_of_stock)) * 100}%` }}
                          >
                            {data.summary.in_stock > 0 && 'In Stock'}
                          </div>
                        )}
                        {data.summary?.out_of_stock > 0 && (
                          <div
                            className="bg-red-500 flex items-center justify-center text-white text-xs font-medium"
                            style={{ width: `${(data.summary.out_of_stock / (data.summary.in_stock + data.summary.out_of_stock)) * 100}%` }}
                          >
                            {data.summary.out_of_stock > 0 && 'OOS'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Alerts */}
                    <div className="space-y-3">
                      {data.summary?.out_of_stock > 0 && (
                        <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                          <PackageX className="h-5 w-5 text-red-600" />
                          <div>
                            <p className="font-medium text-red-900">
                              {data.summary.out_of_stock} products are out of stock
                            </p>
                            <p className="text-sm text-red-700">
                              These products are visible but cannot be purchased
                            </p>
                          </div>
                        </div>
                      )}

                      {data.summary?.low_stock > 0 && (
                        <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                          <TrendingDown className="h-5 w-5 text-yellow-600" />
                          <div>
                            <p className="font-medium text-yellow-900">
                              {data.summary.low_stock} products have low stock (3 or fewer)
                            </p>
                            <p className="text-sm text-yellow-700">
                              Consider restocking these items soon
                            </p>
                          </div>
                        </div>
                      )}

                      {data.empty_categories?.length > 0 && (
                        <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                          <FolderOpen className="h-5 w-5 text-orange-600" />
                          <div>
                            <p className="font-medium text-orange-900">
                              {data.empty_categories.length} active categories have no products
                            </p>
                            <p className="text-sm text-orange-700">
                              Empty categories may make your site look incomplete
                            </p>
                          </div>
                        </div>
                      )}

                      {data.summary?.out_of_stock === 0 && data.summary?.low_stock === 0 && (
                        <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                          <PackageCheck className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="font-medium text-green-900">
                              All products are well stocked!
                            </p>
                            <p className="text-sm text-green-700">
                              No stock issues detected
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Out of Stock Tab */}
                {activeTab === 'oos' && (
                  <div>
                    {data.out_of_stock_products?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {data.out_of_stock_products.map((product) => (
                              <tr key={product.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm">
                                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">{product.sku}</code>
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{product.name}</td>
                                <td className="px-4 py-3 text-sm text-gray-500 capitalize">{product.type}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  ${parseFloat(product.price || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <PackageCheck className="h-12 w-12 mx-auto mb-4 text-green-300" />
                        <p>No out of stock products found</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Low Stock Tab */}
                {activeTab === 'low' && (
                  <div>
                    {data.low_stock_products?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {data.low_stock_products.map((product) => (
                              <tr key={product.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm">
                                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">{product.sku}</code>
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{product.name}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                                    {product.qty} left
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500 capitalize">{product.type}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <PackageCheck className="h-12 w-12 mx-auto mb-4 text-green-300" />
                        <p>No low stock products found</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Categories Tab */}
                {activeTab === 'categories' && (
                  <div>
                    {data.categories?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Products</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {data.categories.map((category) => (
                              <tr
                                key={category.id}
                                className={`hover:bg-gray-50 ${category.product_count === 0 ? 'bg-orange-50' : ''}`}
                              >
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {'—'.repeat(Math.max(0, category.level - 2))} {category.name}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">{category.level}</td>
                                <td className="px-4 py-3 text-sm">
                                  {category.product_count === 0 ? (
                                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                                      Empty
                                    </span>
                                  ) : (
                                    <span className="text-gray-900">{category.product_count}</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  {category.is_active ? (
                                    <span className="text-green-600">Active</span>
                                  ) : (
                                    <span className="text-gray-400">Inactive</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <FolderOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>No category data available</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="mt-6 rounded-lg bg-blue-50 p-4 border border-blue-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">About this data</p>
                  <p>Stock data is retrieved from Magento's product API. Low stock threshold is set to 3 units. Empty categories may impact customer experience and should be reviewed.</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StockStatus;
